import { SearchRegistry } from '../types';

/**
 * Security & Compliance (ticket 2026-10-19-security-and-compliance-section). German keywords are
 * listed because the AI docs search matches substrings only (D-07): a partner asking in German
 * ("IP-Adressen whitelisten", "DSGVO") must still hit these pages.
 */
export const securityAndComplianceSearchEntries: SearchRegistry = {
  '/security-and-compliance/network-and-ip-allowlist': {
    keywords: [
      'network',
      'security',
      'network and security',
      'network and ip allowlist',
      'security and compliance',
      'firewall',
      'ip whitelist',
      'ip allowlist',
      'ip addresses',
      'ryd ip addresses',
      'static egress ip',
      'tls',
      'mtls',
      'security policy',
      'connectivity',
      'staging egress ip',
      'production egress ip',
      'tls 1.2',
      'cert pinning',
      'ip whitelisting',
      'allowlisting',
      'outbound ip',
      'ryd outbound addresses',
      'webhook ip',
      'webhook source ip',
      'callback ip',
      'inbound ip allowlist',
      'inbound allowlist',
      'inbound requests',
      'inbound api access',
      'source ip',
      'cidr',
      'cidr range',
      'ip range',
      'subdomain',
      'dedicated subdomain',
      'partner subdomain',
      'api subdomain',
      'shared endpoint',
      'rydapi.com',
      'rydapi-test.com',
      'x-txn-initiated-by-partner',
      'partner header',
      'rate limit',
      'rate limits',
      'access control',
      'reconciliation files',
      'sftp',
      'inbound',
      'outbound',
      'traffic direction',
      'both directions',
      'partner to ryd',
      'ryd to partner',
      'allowlist status',
      'fleet api subdomain',
      'fleet api ip allowlist',
      'rydpay api ip allowlist',
      'ip freigabe',
      'ip freischalten',
      'firewall freischaltung',
      'ip-adressen',
      'ip adressen',
      'ip-adressen whitelisten',
      'whitelisten',
      'freigeben',
      'firewall freigeben',
      'quell-ip',
      'absender-ip',
      'ausgehende ip',
      'webhook absender',
    ],
    questions: [
      'Which IPs do I need to allowlist for ryd?',
      'What are the IP addresses of ryd?',
      'Was sind die IP-Adressen von ryd, wenn ich sie whitelisten möchte?',
      'Welche IP-Adressen muss ich für ryd in der Firewall freigeben?',
      'What are the network and security requirements for the integration?',
      'Does ryd support mTLS?',
      'What is the staging egress IP for ryd?',
      'Does ryd need to allowlist my IP addresses before I can call the rydpay API?',
      'Does ryd need to allowlist my IP addresses before I can call the Fleet API?',
      'Can I use a dedicated subdomain for the Fleet API?',
      'Does ryd restrict inbound API requests by IP?',
      'Can I get a dedicated API subdomain with its own rate limits?',
      'How do I send ryd my source IPs or CIDR ranges?',
      'Are the IPs on the Network & IP Allowlist page inbound or outbound?',
      'Is there an IP whitelist between ryd and my systems, and in which direction?',
    ],
    faqs: [
      {
        q: 'Is there an IP allowlist for traffic between my systems and ryd?',
        a: 'It depends on the direction. From ryd to you (webhooks, callbacks, report and reconciliation files), ryd sends from fixed published addresses, and an allowlist exists only on your side if your firewall restricts incoming traffic. From you to ryd (requests to the rydpay APIs or the ryd Fleet APIs), there is no IP allowlist by default; one exists only where ryd has enabled it on a dedicated partner subdomain, which is available for both APIs.',
      },
      {
        q: 'Does ryd allowlist my IP addresses for requests to the ryd APIs?',
        a: "Not by default. ryd keeps no inbound IP allowlist; access is controlled by authentication, which differs per API. On the rydpay APIs it is your partner API key and token together with the mandatory x-txn-initiated-by-partner header, which also applies your partner-specific rate limits. On the ryd Fleet APIs it is an OAuth2 access token sent as a Bearer token. The IPs on the Network & IP Allowlist page are ryd's outbound addresses only, for you to allowlist on your side.",
      },
      {
        q: 'Can ryd restrict my API access to my own IP addresses?',
        a: 'Yes, on a dedicated partner subdomain ([partner].rydapi.com for production, [partner].rydapi-test.com for staging), which is the recommended production setup and works for both the rydpay APIs and the ryd Fleet APIs. Each subdomain has its own security configuration and rate limits, independent of other partners, and ryd can enable an inbound IP allowlist on it. Send your source IPs or CIDR ranges per environment to your ryd account manager; the subdomain then appears in Account Settings.',
      },
      {
        q: 'Which static egress IPs do I allowlist for ryd?',
        a: "Security & Compliance → Network & IP Allowlist lists ryd's current outbound IPs per environment (production, staging, development) plus ryd's corporate support IPs; the addresses come from the page's data file, which is also in this documentation. Allowlist every address of each environment you use. The same page shows what ryd has recorded for your own account: whether those IPs are allowlisted for webhooks, for report delivery, or not required.",
      },
    ],
  },

  '/security-and-compliance/hosting-and-infrastructure': {
    keywords: [
      'hosting',
      'hosting location',
      'server location',
      'data center',
      'eu hosting',
      'data residency',
      'datenstandort',
      'rechenzentrum',
      'cloudflare',
      'data localization suite',
      'dls',
      'tls termination',
      'eu data localization',
      'ddos protection',
      'waf',
      'google cloud',
      'gcp',
      'gridscale',
      'firebase hosting',
      'frankfurt',
      'private network',
      'outgoing traffic',
      'egress',
      'disaster recovery',
      'backups',
      'security and compliance',
    ],
    questions: [
      'Where does ryd host my data?',
      'Wo hostet ryd meine Daten?',
      'Where is TLS terminated for ryd domains and APIs?',
      'Does ryd use the Cloudflare Data Localization Suite?',
      'Does traffic to ryd stay in the EU?',
      'Where does outgoing traffic from ryd come from?',
      'Which region does ryd use on Google Cloud?',
      'Does any part of the ryd platform run outside Europe?',
    ],
    faqs: [
      {
        q: 'Where does ryd host my data?',
        a: 'In Europe only. Core applications, databases and backend services run on gridscale in Germany (primary), Austria and Switzerland; ryd services on Google Cloud run in Germany or elsewhere in Europe; disaster-recovery backups are kept in Germany; web frontends, the ryd portal and the CDN run in Belgium, Frankfurt and Zurich. See Security & Compliance → Hosting & Infrastructure.',
      },
      {
        q: 'Does traffic to ryd stay in the EU?',
        a: "Yes. All traffic to ryd's domains and API gateways passes through Cloudflare with the Data Localization Suite active: TLS terminates only in Cloudflare data centres in the EU, and request metadata such as logs and analytics is processed and stored in the EU.",
      },
      {
        q: 'Where does outgoing traffic from ryd come from?',
        a: 'Every ryd service is attached to a private network, and all outgoing traffic (webhooks, callbacks, file transfers, calls to partner APIs) leaves from a data centre in Frankfurt, Germany, through fixed IP addresses. The addresses are listed on Security & Compliance → Network & IP Allowlist.',
      },
    ],
  },

  '/security-and-compliance/service-providers': {
    keywords: [
      'service providers',
      'infrastructure providers',
      'sub-processor',
      'subprocessor',
      'sub-processors',
      'unterauftragsverarbeiter',
      'dienstleister',
      'hosting provider',
      'hosting-anbieter',
      'cloud provider',
      'gridscale',
      'ovhcloud',
      'google cloud',
      'gcp',
      'cloudflare',
      'firebase hosting',
      'odoo cloud',
      'odoo.sh',
      'odoo',
      'hetzner',
      'iso 27001',
      'iso 27017',
      'iso 27018',
      'soc 2',
      'bsi c5',
      'c5',
      'pci dss',
      'csa star',
      'certifications',
      'zertifizierungen',
      'security and compliance',
    ],
    questions: [
      'Which cloud and hosting providers does ryd use?',
      'Welche Hosting-Anbieter und Dienstleister nutzt ryd?',
      'Which sub-processors does ryd use?',
      "Which certifications do ryd's hosting providers have?",
      "Is ryd's hosting ISO 27001 or BSI C5 certified?",
      "Where is ryd's Odoo hosted?",
      'Does ryd use Cloudflare, Google Cloud or Hetzner?',
    ],
    faqs: [
      {
        q: 'Which service providers run the ryd platform?',
        a: 'gridscale (OVHcloud group) for the core platform, Google Cloud for backend services, analytics, the private network and outgoing traffic, Cloudflare as edge network, Firebase Hosting for the web frontends, Odoo Cloud (Odoo.sh) as ERP, and Hetzner for development and staging test servers. All of them process ryd data in Germany or elsewhere in Europe under a GDPR data processing agreement. Details per provider are on Security & Compliance → Service Providers; the full sub-processor list comes from your ryd account manager.',
      },
      {
        q: 'What does gridscale do for ryd?',
        a: 'gridscale (OVHcloud group) is the main platform for the ryd core services: business applications, databases and backend services, including customer accounts, orders, transactions and authentication. Locations: Germany (primary), Austria and Switzerland. Certifications: ISO/IEC 27001 (incl. NIS 2), ISO 9001, BSI C5.',
      },
      {
        q: 'What does Google Cloud do for ryd?',
        a: 'Google Cloud runs ryd backend services, analytics, the private network between ryd services and the outgoing traffic. Locations: Germany (Frankfurt, primary) and other European regions; outgoing traffic leaves from Frankfurt. Certifications: ISO/IEC 27001, 27017, 27018, SOC 1/2/3, PCI DSS, BSI C5.',
      },
      {
        q: 'What does Cloudflare do for ryd?',
        a: 'Cloudflare is the edge network for all ryd domains and API gateways: DDoS protection, WAF, load balancing and CDN. The Data Localization Suite is active, so TLS termination and request metadata stay in the EU. Certifications: ISO/IEC 27001, 27701, 27018, SOC 2 Type II, PCI DSS Level 1, BSI C5, EU Cloud Code of Conduct.',
      },
      {
        q: 'What does Firebase Hosting do for ryd?',
        a: 'Firebase Hosting (Google) delivers the web frontends, including the ryd portal, from Europe through the Cloudflare edge with EU TLS termination. It is covered by the Google Cloud certifications: ISO/IEC 27001, 27017, 27018, SOC 1/2/3, PCI DSS.',
      },
      {
        q: "Where is ryd's Odoo hosted?",
        a: 'ryd uses Odoo Cloud (Odoo.sh) as ERP for invoicing, billing and accounting of business and fleet customers, in the Odoo Europe region: production in France and Belgium, backups in France, the Netherlands and Sweden. Odoo holds CSA STAR Level 1; its infrastructure (Google Cloud, OVHcloud) holds ISO/IEC 27001, SOC 1/2 Type II, PCI DSS and CSA STAR.',
      },
      {
        q: 'What does Hetzner do for ryd?',
        a: 'Hetzner hosts development and staging test servers, such as mock servers and test delivery, in Germany only (Falkenstein, Nuremberg). Certifications: ISO/IEC 27001:2022, BSI C5 Type 2.',
      },
    ],
  },

  '/security-and-compliance/data-protection': {
    keywords: [
      'gdpr',
      'dsgvo',
      'data protection',
      'datenschutz',
      'privacy',
      'data privacy',
      'data processing agreement',
      'dpa',
      'avv',
      'auftragsverarbeitung',
      'auftragsverarbeitungsvertrag',
      'article 28',
      'art. 28',
      'sub-processor list',
      'data retention',
      'aufbewahrung',
      'delete driver data',
      'erase personal data',
      'löschung',
      'bsi',
      'ai activity log',
      'security and compliance',
    ],
    questions: [
      'Is ryd GDPR compliant?',
      'Ist ryd DSGVO-konform?',
      'How do I get a data processing agreement (DPA / AVV) with ryd?',
      'How do I get the full sub-processor list?',
      'What happens to driver data when I remove a driver?',
      'How long does ryd keep the AI activity log?',
    ],
    faqs: [
      {
        q: 'How do I get a data processing agreement with ryd?',
        a: 'ryd concludes data processing agreements under Article 28 GDPR with its partners. Your ryd account manager provides the agreement for your company; see Security & Compliance → Data Protection & GDPR.',
      },
      {
        q: 'How do I get the full sub-processor list?',
        a: 'The providers of hosting, edge network, cloud services and ERP are listed on Security & Compliance → Service Providers. The full sub-processor list, including providers for business functions such as email delivery or monitoring, comes from your ryd account manager on request.',
      },
      {
        q: 'Does removing a driver delete all of their data?',
        a: 'No. Removing a driver ends the active fleet assignment and makes the driver account inactive for that fleet. Historical business records such as orders and invoice references are retained for reconciliation, accounting and audit. Requests to erase or export personal data go to your ryd account manager or ryd support.',
      },
    ],
  },
};
